import { Injectable } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';

export const SESSION_CHANNEL = 'mib-session-sync';

const RESPONSE_TIMEOUT_MS = 150;

type SessionDump = Record<string, string>;
type ChannelMessage =
  | { type: 'WRITE'; key: string; value: string }
  | { type: 'REMOVE'; key: string }
  | { type: 'CLEAR' }
  | { type: 'REQUEST' }
  | { type: 'SESSION'; data: SessionDump };
interface AuthBlob {
  authnResult?: { access_token?: string };
  storageSilentRenewRunning?: string;
}

let channel: BroadcastChannel | null | undefined;

/** The one channel of this tab, kept open for its whole life. */
export function sessionChannel(): BroadcastChannel | null {
  channel ??=
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SESSION_CHANNEL);
  return channel;
}

function parseJson<T>(value: string | null | undefined): T | null {
  try {
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
}

function hasSession(value: string): boolean {
  return !!parseJson<AuthBlob>(value)?.authnResult?.access_token;
}

function isRefreshRunning(value: string | null): boolean {
  // The library stores the lock as a JSON string nested in the blob.
  const lock = parseJson<AuthBlob>(value)?.storageSilentRenewRunning;
  return parseJson<{ state?: string }>(lock)?.state === 'running';
}

function dumpSessionStorage(): SessionDump {
  const dump: SessionDump = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    const value = key === null ? null : sessionStorage.getItem(key);
    if (key !== null && value !== null) dump[key] = value;
  }
  return dump;
}

/** Keeps the OIDC session in sessionStorage, mirrored to the other open tabs. */
@Injectable()
export class SharedSessionStorage implements AbstractSecurityStorage {
  private readonly channel = sessionChannel();

  constructor() {
    this.channel?.addEventListener('message', (event: MessageEvent<ChannelMessage>) =>
      this.applyFromOtherTab(event.data),
    );
  }

  read(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  write(key: string, value: string): void {
    const signedOut = hasSession(sessionStorage.getItem(key) ?? '') && !hasSession(value);
    sessionStorage.setItem(key, value);
    // In-flight sign-in data and the renew lock stay local so tabs cannot overwrite each other.
    if (hasSession(value)) this.post({ type: 'WRITE', key, value });
    // Sign-out writes the blob without tokens instead of removing it: tell the other tabs.
    else if (signedOut) this.post({ type: 'REMOVE', key });
  }

  remove(key: string): void {
    sessionStorage.removeItem(key);
    this.post({ type: 'REMOVE', key });
  }

  clear(): void {
    sessionStorage.clear();
    this.post({ type: 'CLEAR' });
  }

  private post(message: ChannelMessage): void {
    this.channel?.postMessage(message);
  }

  private applyFromOtherTab(message: ChannelMessage | undefined): void {
    switch (message?.type) {
      case 'WRITE':
        // A renew in flight here would reuse the refresh token the incoming blob replaced.
        if (isRefreshRunning(sessionStorage.getItem(message.key))) return;
        return sessionStorage.setItem(message.key, message.value);
      case 'REMOVE':
        return sessionStorage.removeItem(message.key);
      case 'CLEAR':
        return sessionStorage.clear();
    }
  }
}

/** Answers other tabs for this tab's life and, when empty, copies the session of an open tab first. */
export function restoreSessionFromOtherTabs(): Promise<void> {
  const tabs = sessionChannel();
  if (!tabs) return Promise.resolve();
  tabs.addEventListener('message', (event: MessageEvent<ChannelMessage>) => {
    if (event.data?.type !== 'REQUEST') return;
    tabs.postMessage({ type: 'SESSION', data: dumpSessionStorage() } satisfies ChannelMessage);
  });
  if (sessionStorage.length > 0) return Promise.resolve();

  return new Promise<void>((resolve) => {
    const onSession = (event: MessageEvent<ChannelMessage>) => {
      const message = event.data;
      if (message?.type !== 'SESSION' || !Object.values(message.data).some(hasSession)) return;
      for (const [key, value] of Object.entries(message.data)) sessionStorage.setItem(key, value);
      finish();
    };
    const finish = () => {
      clearTimeout(timer);
      tabs.removeEventListener('message', onSession);
      resolve();
    };
    const timer = setTimeout(finish, RESPONSE_TIMEOUT_MS);
    tabs.addEventListener('message', onSession);
    tabs.postMessage({ type: 'REQUEST' } satisfies ChannelMessage);
  });
}
